import { ReportGroup } from '@/api/generatedApi';
import { Autocomplete, Box, Typography, CircularProgress } from '@mui/material';
import { pluralize } from '@/utils/format';
import { TextInput } from '@/components/Inputs/TextInput';
import { AssetThumbnailGrid } from '@/components/AssetThumbnailGrid/AssetThumbnailGrid';

interface GroupOption {
  id: string;
  displayName: string;
  assetCount: number;
  assetIds: string[];
  tenantId: string;
}

export const DestinationGroupAutocomplete = ({
  availableGroups,
  allGroups,
  selectedGroupId,
  onGroupSelect,
  isLoadingGroups,
}: {
  availableGroups: ReportGroup[];
  allGroups: ReportGroup[];
  selectedGroupId: string | undefined;
  onGroupSelect: (groupId: string | undefined) => void;
  isLoadingGroups: boolean;
}) => {
  const options: GroupOption[] = availableGroups.map(group => {
    const originalIndex = allGroups.findIndex(g => g.id === group.id);
    const displayName = group.name || `Group ${originalIndex + 1}`;
    return {
      id: group.id,
      displayName,
      assetCount: group.assetIds.length,
      assetIds: group.assetIds,
      tenantId: group.tenantId,
    };
  });

  const selectedOption = options.find(opt => opt.id === selectedGroupId);

  return (
    <Autocomplete
      options={options}
      // eslint-disable-next-line unicorn/no-null -- MUI Autocomplete requires null (not undefined) for controlled components
      value={selectedOption || null}
      onChange={(_event, newValue) => onGroupSelect(newValue?.id)}
      loading={isLoadingGroups}
      getOptionLabel={option => option.displayName}
      filterOptions={(options, state) => {
        const inputValue = state.inputValue.toLowerCase();
        if (!inputValue) return options;
        return options.filter(option => option.displayName.toLowerCase().includes(inputValue));
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={parameters => (
        <TextInput
          {...parameters}
          placeholder='Search for a group...'
          slotProps={{
            input: {
              ...parameters.InputProps,
              endAdornment: (
                <>
                  {isLoadingGroups && <CircularProgress color='inherit' size={20} />}
                  {parameters.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(properties, option) => (
        <li {...properties} key={option.id}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%', py: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{option.displayName}</Typography>
            <Typography variant='body2' color='text.secondary' sx={{ fontSize: '0.875rem', mb: 0.5 }}>
              {pluralize(option.assetCount, 'asset', 'assets')}
            </Typography>
            <AssetThumbnailGrid assetIds={option.assetIds} tenantId={option.tenantId} />
          </Box>
        </li>
      )}
      noOptionsText='No groups available'
    />
  );
};
